import React, { useEffect, useLayoutEffect, useState } from "react"

import {
  styled,
  Typography,
  useTheme
} from "@mui/material"

import { ChevronRightRounded } from "@mui/icons-material"

import HKVLink from "../HKVLink"

import { isValidHttpUrl } from "../../lib/utils"

const StyledCard = styled("div", {
  shouldForwardProp: (prop) => prop !== "columns" || prop !== "footer",
})(({ theme, columns, footer }) => {
  const borderWidth = 1
  const gutterSize = columns >= 5 ? theme.spacing(1) : "var(--size-gutter)"
  const calculatedWidth = `calc(100% / ${columns || 3} - (${gutterSize} / ${columns} * ${columns - 1} + ${borderWidth * 2}px))`

  return {
    display: "flex",
    flexDirection: "column",
    borderRadius: theme.shape.borderRadius,
    cursor: "pointer",

    border: `${borderWidth}px solid #e6e6e6`,
    width: calculatedWidth,
    [theme.breakpoints.down("lg")]: {
      width: `calc(100% / ${3} - (${gutterSize} / ${3} * ${2} + ${borderWidth * 2}px))`
    },
    [theme.breakpoints.down("md")]: {
      width: `calc(100% / ${2} - (${gutterSize} / ${2} * ${1} + ${borderWidth * 2}px))`
    },
    [theme.breakpoints.down("sm")]: {
      width: "100%"
    },

    "& .card-content": {
      display: "flex",
      flexDirection: "column",
    },
    "& .card-content-head": {
      paddingTop: "56.25%",
      position: "relative",
      zIndex: 1
    },
    "& .card-content-body": {
      padding: footer ? theme.spacing(2, 2, 0, 2) : theme.spacing(2),
      [theme.breakpoints.down("sm")]: {
        padding: footer ? theme.spacing(1, 1, 0, 1) : theme.spacing(1)
      },
      ...columns >= 5 && {
        padding: footer ? theme.spacing(1, 1, 0, 1) : theme.spacing(1)
      }
    },
    "& .card-content-body-title": {
      color: theme.palette.hemelblauw.main,
      fontWeight: 700,
      ...columns >= 5 && {
        fontSize: theme.typography.pxToRem(16)
      }
    },
    "& .card-content-body-text": {
      marginTop: theme.spacing(1),
      ...columns >= 5 && {
        fontSize: theme.typography.pxToRem(15)
      }
    },
    "& .card-footer": {
      textDecoration: "none",
      padding: theme.spacing(2, 2.5, 2.5, 2.5),
      color: theme.palette.hemelblauw.main,
      fontWeight: 700,
      [theme.breakpoints.down("md")]: {
        padding: theme.spacing(2)
      },
      [theme.breakpoints.down("sm")]: {
        padding: theme.spacing(1)
      }
    },

    "&:hover": {
      "& .card-content-body-title": {
        textDecoration: "underline",
        textDecorationThickness: 2,
        color: theme.palette.lintblauw.main
      },
      "& .card-footer": {
        textDecoration: "underline",
        textDecorationThickness: 1,
        color: theme.palette.lintblauw.main
      }
    }
  }
})

const Card = (props) => {
  const {
    card,
    columns,
    createLink,
    footer = true
  } = props

  const theme = useTheme()

  return (
    <StyledCard className="card" columns={columns}>
      <HKVLink
        to={createLink(card)}
        sx={{
          "&:hover": {
            "& .card-content-body-text": {
              textDecoration: "none"
            }
          }
        }}
        footer={footer}
      >
        <div className="card-content">
          <div className="card-content-head">
            <picture className="card-content-head-image">
              <img
                src={isValidHttpUrl(card.image_url) ? card.image_url : `/static/images/theme_images/${card.image_url}`}
                title={card.name}
                alt={card.image_url}
                width={1200} height={674}
              />
            </picture>
          </div>
          <div className="card-content-body">
            <Typography className="card-content-body-title" variant="h3">{card.name}</Typography>
            <Typography className="card-content-body-text">{card.description}</Typography>
          </div>
        </div>
        {footer && <div className="card-footer">
          <Typography sx={{ display: "flex", alignItems: "center" }}>
            Meer over {card.name} <ChevronRightRounded sx={{ marginLeft: theme.spacing(0.5) }} />
          </Typography>
        </div>}
      </HKVLink>
    </StyledCard>
  )
}

export default React.memo(Card)